/**
 * The workspace's recycle bin setting: keep deleted assets for a number of days, or purge them after a short undo
 * window. Edited in Administration → System status → Housekeeping (admins only, `PUT /api/v1/settings/recycle-bin`);
 * the Recycle bin page shows it as one read-only line.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import type { RecycleBinSettingsDTO } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useRecycleSettings } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import { toast } from './Toasts';
import { relative } from '../lib/format';

/** Where the setting is edited; the anchor opens System status on Housekeeping and scrolls to the section. */
export const RECYCLE_SETTINGS_HREF = '/admin/status#recycle-bin';

function undoText(s: RecycleBinSettingsDTO) {
  const minutes = Math.max(1, Math.round(s.undoSeconds / 60));
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/** One line for everyone on the Recycle bin page, with a link to the setting for admins. */
export function RecycleBinSummary({ settings, isAdmin }: { settings: RecycleBinSettingsDTO; isAdmin: boolean }) {
  return (
    <p className="muted" style={{ margin: '0 0 12px' }} data-testid="recycle-summary">
      {settings.enabled
        ? `Deleted assets are kept for ${settings.retentionDays} day${settings.retentionDays === 1 ? '' : 's'}, then purged.`
        : `The recycle bin is off: deletes can be undone for ${undoText(settings)}, then the asset is purged.`}
      {isAdmin && <> <Link to={RECYCLE_SETTINGS_HREF} data-testid="recycle-settings-link">Change in Administration → System status → Housekeeping</Link></>}
    </p>
  );
}

/** The editable section in Housekeeping. */
export function RecycleBinSettings() {
  const q = useRecycleSettings();
  if (!q.data) return <div className="skeleton" style={{ height: 120, maxWidth: 760, marginTop: 16 }} />;
  return <RecycleBinSettingsForm settings={q.data} />;
}

function RecycleBinSettingsForm({ settings }: { settings: RecycleBinSettingsDTO }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [days, setDays] = useState(settings.retentionDays);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setEnabled(settings.enabled); setDays(settings.retentionDays); }, [settings.enabled, settings.retentionDays]);
  const dirty = enabled !== settings.enabled || days !== settings.retentionDays;

  async function save() {
    setSaving(true);
    try {
      await api('/api/v1/settings/recycle-bin', { method: 'PUT', json: { enabled, retentionDays: days } });
      await qc.invalidateQueries({ queryKey: queryKeys.recycleBins() });
      await qc.invalidateQueries({ queryKey: queryKeys.recycleSettings() });
      toast('Recycle bin settings saved');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section id="recycle-bin" className="panel-card" style={{ maxWidth: 760, marginTop: 16, scrollMarginTop: 16 }} data-testid="recycle-settings">
      <p className="section-label">Recycle bin</p>
      <div className="segmented" role="radiogroup" aria-label="Recycle bin">
        <button aria-pressed={enabled} onClick={() => setEnabled(true)}>Keep deleted assets in the recycle bin</button>
        <button aria-pressed={!enabled} onClick={() => setEnabled(false)}>Delete after a short undo window</button>
      </div>
      {enabled ? (
        <div className="field" style={{ maxWidth: 240 }}>
          <label htmlFor="retention">Keep for (days)</label>
          <input id="retention" className="input" type="number" min={1} max={3650} value={days} onChange={(e) => setDays(Math.max(1, Math.min(3650, Number(e.target.value) || 1)))} />
          <span className="help">Changing this also applies to assets already in the bin.</span>
        </div>
      ) : (
        <span className="muted">Deletes can be undone for {undoText(settings)}, then the asset is purged.</span>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn primary" disabled={!dirty || saving} onClick={() => void save()}>{saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Save</button>
        {settings.updatedAt && <span className="faint" style={{ fontSize: 12 }}>Last changed {relative(settings.updatedAt)}</span>}
      </div>
    </section>
  );
}
